import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Text,
  Stack,
  Heading,
  Button,
  SimpleGrid,
  useDisclosure,
  Portal,
  Dialog,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";

// Modern, glassy coin design with unique gradient IDs for dynamic color
const ModernCoin = ({
  face,
  color,
  rim,
  icon,
}: {
  face: string;
  color: string;
  rim: string;
  icon?: string;
}) => {
  // Generate a unique id for each coin instance to avoid gradient conflicts
  const uniqueId = React.useId() || Math.random().toString(36).substr(2, 9);
  const rimId = `rim-${uniqueId}`;
  const faceId = `face-${uniqueId}`;
  const shineId = `shine-${uniqueId}`;
  return (
    <svg width="100%" height="100%" viewBox="0 0 120 120">
      <defs>
        <radialGradient id={rimId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff" />
          <stop offset="80%" stopColor={rim} />
          <stop offset="100%" stopColor="#222" />
        </radialGradient>
        <radialGradient id={faceId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff" />
          <stop offset="100%" stopColor={color} />
        </radialGradient>
        <linearGradient id={shineId} x1="0" y1="0" x2="120" y2="120">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <circle
        cx="60"
        cy="60"
        r="58"
        fill={`url(#${rimId})`}
        stroke={rim}
        strokeWidth="2"
      />
      <circle cx="60" cy="60" r="50" fill={`url(#${faceId})`} />
      <ellipse
        cx="45"
        cy="45"
        rx="18"
        ry="8"
        fill={`url(#${shineId})`}
        opacity="0.5"
      />
      <text
        x="60"
        y="70"
        textAnchor="middle"
        fontSize="56"
        fill="#fff"
        fontWeight="bold"
      >
        {icon ? (face === "Heads" ? icon : "T") : face === "Heads" ? "H" : "T"}
      </text>
    </svg>
  );
};

const newCoinDesigns = [
  {
    key: "glass-gold",
    label: "Glassy Gold",
    color: "#ffe066",
    rim: "#a67c00",
    icon: "🏏",
  },
  {
    key: "glass-blue",
    label: "Glassy Blue",
    color: "#00b4d8",
    rim: "#03045e",
    icon: "🌟",
  },
  {
    key: "glass-green",
    label: "Glassy Green",
    color: "#43e97b",
    rim: "#14532d",
    icon: "🍀",
  },
  {
    key: "glass-pink",
    label: "Glassy Pink",
    color: "#ff61a6",
    rim: "#7c1c48",
    icon: "🎀",
  },
  {
    key: "glass-orange",
    label: "Glassy Orange",
    color: "#ffb347",
    rim: "#ff7300",
    icon: "🍊",
  },
  {
    key: "glass-purple",
    label: "Glassy Purple",
    color: "#a259f7",
    rim: "#4b006e",
    icon: "🔮",
  },
  {
    key: "glass-red",
    label: "Glassy Red",
    color: "#ff4e50",
    rim: "#b71c1c",
    icon: "🍎",
  },
  {
    key: "glass-cyan",
    label: "Glassy Cyan",
    color: "#00fff0",
    rim: "#00796b",
    icon: "💎",
  },
  {
    key: "glass-black",
    label: "Glassy Black",
    color: "#222",
    rim: "#444",
    icon: "🖤",
  },
  {
    key: "glass-white",
    label: "Glassy White",
    color: "#fff",
    rim: "#e0e0e0",
    icon: "🤍",
  },
  {
    key: "glass-brown",
    label: "Glassy Brown",
    color: "#a0522d",
    rim: "#6e3b1e",
    icon: "🥥",
  },
  {
    key: "glass-sunset",
    label: "Glassy Sunset",
    color: "#ff7e5f",
    rim: "#feb47b",
    icon: "🌅",
  },
  {
    key: "glass-fire",
    label: "Glassy Fire",
    color: "#ff9800",
    rim: "#d84315",
    icon: "🔥",
  },
  {
    key: "glass-ice",
    label: "Glassy Ice",
    color: "#e0f7fa",
    rim: "#00bcd4",
    icon: "❄️",
  },
  {
    key: "glass-leaf",
    label: "Glassy Leaf",
    color: "#a5d6a7",
    rim: "#388e3c",
    icon: "🍃",
  },
];

const modernFlipAnim = keyframes`
  0% {
    transform: rotateY(0deg) scale(1) translateY(0px);
    filter: drop-shadow(0 0 0 #fff);
    box-shadow: 0 0 0 #fff;
  }
  10% {
    transform: rotateY(180deg) scale(1.08) translateY(-8px);
    filter: drop-shadow(0 0 8px #ffe066);
    box-shadow: 0 0 8px #ffe066;
  }
  25% {
    transform: rotateY(540deg) scale(1.15) translateY(-16px);
    filter: drop-shadow(0 0 16px #00b4d8);
    box-shadow: 0 0 16px #00b4d8;
  }
  50% {
    transform: rotateY(900deg) scale(1.22) translateY(-24px);
    filter: drop-shadow(0 0 32px #ff61a6);
    box-shadow: 0 0 32px #ff61a6;
  }
  75% {
    transform: rotateY(1260deg) scale(1.15) translateY(-16px);
    filter: drop-shadow(0 0 16px #43e97b);
    box-shadow: 0 0 16px #43e97b;
  }
  90% {
    transform: rotateY(1620deg) scale(1.08) translateY(-8px);
    filter: drop-shadow(0 0 8px #ffe066);
    box-shadow: 0 0 8px #ffe066;
  }
  100% {
    transform: rotateY(1800deg) scale(1) translateY(0px);
    filter: drop-shadow(0 0 0 #fff);
    box-shadow: 0 0 0 #fff;
  }
`;

const NewTossPage: React.FC = () => {
  const navigate = useNavigate();
  const [flipping, setFlipping] = useState(false);
  const [side, setSide] = useState<string | null>(null);
  const [flipKey, setFlipKey] = useState(0);
  const [selectedCoin, setSelectedCoin] = useState<string>(
    newCoinDesigns[0].key
  );
  const { open, onOpen, onClose } = useDisclosure();

  const flipCoin = () => {
    setFlipping(true);
    setSide(null);
    setFlipKey((k) => k + 1);

    const faces = ["Heads", "Tails"];

    // Random total flip duration (human-like)
    const totalDuration = 900 + Math.random() * 600; // 900–1500ms

    // How fast faces change during spin
    const intervalTime = 100;

    const interval = setInterval(() => {
      // Randomly show heads/tails while spinning
      setSide(faces[Math.floor(Math.random() * 2)]);
    }, intervalTime);

    setTimeout(() => {
      clearInterval(interval);

      // Final result (true randomness)
      const finalSide = Math.random() < 0.5 ? "Heads" : "Tails";

      setSide(finalSide);
      setFlipping(false);
    }, totalDuration);
  };

  const coin = newCoinDesigns.find((c) => c.key === selectedCoin);

  return (
    <Stack
      direction="column"
      gap={{ base: 6, md: 10 }}
      mt={0}
      align="center"
      bgGradient="linear(to-b, #e0eafc, #cfdef3)"
      minH="100vh"
      h="100vh"
      p={{ base: 4, md: 8 }}
      boxShadow={{ base: "none", md: "2xl" }}
      overflow="hidden"
    >
      {/* Top right button to go back to / route */}
      <Box position="absolute" top={4} right={4} zIndex={10}>
        <Button
          variant="ghost"
          colorScheme="teal"
          borderRadius="full"
          p={2}
          minW={0}
          onClick={() => navigate("/")}
          aria-label="Go back to home"
        >
          <Box as="span" fontSize="2xl">
            🏠
          </Box>
        </Button>
      </Box>
      <Heading
        size="2xl"
        color="teal.700"
        letterSpacing={2}
        mb={2}
        fontWeight={800}
        textAlign="center"
      >
        Cricket Toss
      </Heading>
      <Text
        fontSize={{ base: "md", md: "xl" }}
        color="gray.700"
        maxW={480}
        textAlign="center"
        mb={2}
      >
        Flip the virtual coin to decide who bats or bowls first! Tap the coin to
        toss, and use the button below to choose your favorite coin design.
      </Text>
      <Box
        key={flipKey}
        w={{ base: 32, sm: 40, md: 56 }}
        h={{ base: 32, sm: 40, md: 56 }}
        borderRadius="full"
        boxShadow="0 12px 36px 0 rgba(0,0,0,0.18)"
        bgGradient="radial(circle at 60% 40%, #fff 60%, #e0eafc 100%)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        css={
          flipping
            ? {
                perspective: "1000px",
                animation: `${modernFlipAnim} 1.4s cubic-bezier(.4,2,.6,1)`,
                boxShadow: "0 0 32px 8px #ffe066, 0 0 64px 16px #00b4d8",
                zIndex: 2,
              }
            : {
                perspective: "1000px",
                transition: "box-shadow 0.3s, transform 0.3s",
                boxShadow: "0 12px 36px 0 rgba(0,0,0,0.18)",
                zIndex: 1,
              }
        }
        style={{
          cursor: flipping ? "not-allowed" : "pointer",
          opacity: 1,
          transition: "box-shadow 0.3s, transform 0.3s",
        }}
        onClick={() => {
          if (!flipping) flipCoin();
        }}
        tabIndex={0}
        aria-label="Flip coin"
        role="button"
      >
        <Box
          w="96%"
          h="96%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {coin && (
            <ModernCoin
              face={side ? side : "Heads"}
              color={coin.color}
              rim={coin.rim}
              icon={coin.icon}
            />
          )}
        </Box>
      </Box>
      <Box
        minH="40px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {side ? (
          <Text
            minH="40px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {side}
          </Text>
        ) : null}
      </Box>
      <Button
        bgGradient="linear(to-r, #4fc3f7, #01579b)"
        color="#fff"
        _hover={{
          bgGradient: "linear(to-r, #01579b, #4fc3f7)",
          boxShadow: "0 0 0 2px #01579b",
        }}
        _active={{ bg: "#01579b" }}
        fontWeight="bold"
        fontSize="lg"
        borderRadius="full"
        px={8}
        py={6}
        shadow="md"
        onClick={onOpen}
        mb={2}
      >
        <Box as="span" mr={2} fontSize="xl">
          🪙
        </Box>{" "}
        Choose Coin Style
      </Button>
      <Dialog.Root size={{ mdDown: "full", md: "lg" }} open={open}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content
              borderRadius="2xl"
              p={6}
              maxW="96vw"
              width={{ base: "100vw", md: "480px" }}
            >
              <Dialog.Header>
                <Dialog.Title
                  fontSize={{ base: "lg", md: "xl" }}
                  color="teal.700"
                >
                  <Box as="span" fontSize="2xl" mr={2}>
                    🪙
                  </Box>{" "}
                  Select a Coin
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <SimpleGrid columns={{ base: 2, md: 3 }} gap={4}>
                  {newCoinDesigns.map((coin) => (
                    <Box
                      key={coin.key}
                      border={
                        selectedCoin === coin.key
                          ? "2px solid #319795"
                          : "1px solid #eee"
                      }
                      borderRadius="md"
                      p={2}
                      cursor="pointer"
                      onClick={() => {
                        setSelectedCoin(coin.key);
                        onClose();
                      }}
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      transition="box-shadow 0.2s, border 0.2s"
                      boxShadow={
                        selectedCoin === coin.key ? "0 0 0 2px #4fc3f7" : "none"
                      }
                    >
                      <Box w={16} h={16}>
                        <ModernCoin
                          face={side ?? "Heads"}
                          color={coin.color}
                          rim={coin.rim}
                          icon={coin.icon}
                        />
                      </Box>
                      <Text
                        mt={2}
                        fontSize="sm"
                        color="teal.700"
                        fontWeight={
                          selectedCoin === coin.key ? "bold" : "normal"
                        }
                        textAlign="center"
                      >
                        {coin.label}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Stack>
  );
};

export default NewTossPage;
